import React, { useEffect, useState } from 'react';
import { teacherApi } from '../services/api';
import GoalFormFields from './GoalFormFields';
import { dedupSubjectOptionsFromBooks } from '../utils/subjectOptions';
import { emptyGoalForm, goalHasTimeRange, teacherGoalPayload, validateGoalForm, type GoalFormValue } from '../utils/goalForm';

/**
 * Hedef Ata — the teacher's one goal form, for a single student or a whole
 * class: Öğrenci Çalışma Planı, Sınıflarım (öğrenci + sınıf) all open this.
 * The mobile twin is dijipo-mobile/components/goals/GoalAssignModal.tsx; the
 * fields are GoalFormFields, the rules utils/goalForm.ts.
 *
 * 2026-10: the goal is named by the teacher (no fixed "Hedef Türü"), any number
 * of goals may be given, and the "Kitap ödevi de oluştur" option is gone.
 *
 * Before a timed goal is sent, the students' weekly lesson schedule is checked
 * for that hour: a clash is shown with the names and can be overridden. Another
 * GOAL in the slot is refused by the backend, naming whose goal holds it.
 */
export type HedefAtaTarget =
  | { kind: 'student'; uuid: string; name: string; classId?: string }
  | { kind: 'class'; classId: string; name: string };

interface Props {
  open: boolean;
  target: HedefAtaTarget;
  /** Students whose lesson schedule is checked before a timed goal is sent. */
  preflightStudents?: Array<{ uuid: string; name: string }>;
  defaultDate?: string;      // YYYY-MM-DD
  defaultStartTime?: string; // HH:mm
  defaultEndTime?: string;   // HH:mm
  onClose: () => void;
  onSubmitted: () => void;
}

const F: React.CSSProperties = { fontFamily: 'Inter' };
const localIso = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

interface ClassResult { succeeded: number; total: number; conflicts: { student: string; message: string }[] }

const HedefAtaModal: React.FC<Props> = ({
  open, target, preflightStudents, defaultDate, defaultStartTime, defaultEndTime, onClose, onSubmitted,
}) => {
  const [form, setForm] = useState<GoalFormValue>(() => emptyGoalForm('teacher', defaultDate ?? localIso()));
  const [subjects, setSubjects] = useState<Array<{ id: number; name: string }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleClash, setScheduleClash] = useState<string[]>([]);
  const [result, setResult] = useState<ClassResult | null>(null);
  const [assigned, setAssigned] = useState(false);
  const classId = target.classId;

  // A fresh form on every open, so one student's choices never leak into the next.
  useEffect(() => {
    if (!open) return;
    setForm(emptyGoalForm('teacher', defaultDate ?? localIso(), {
      ...(defaultStartTime ? { startTime: defaultStartTime } : {}),
      ...(defaultEndTime ? { endTime: defaultEndTime } : {}),
    }));
    setError(null);
    setScheduleClash([]);
    setResult(null);
    setAssigned(false);
    // Ders options come from the teacher's own books (branch + the class's level).
    teacherApi.booksForAssignment(classId)
      .then((r: any) => setSubjects(dedupSubjectOptionsFromBooks((r.data ?? []).map((b: any) => ({
        id: b.id, title: b.title, subjectId: b.subjectId ?? b.subject?.id ?? null, subject: b.subject ?? null,
      })))))
      .catch(() => setSubjects([]));
  }, [open, defaultDate, defaultStartTime, defaultEndTime, classId]);

  if (!open) return null;

  const patch = (p: Partial<GoalFormValue>) => { setForm((f) => ({ ...f, ...p })); setError(null); setScheduleClash([]); };

  // Weekly lesson schedule at that hour, per student (the backend checks other goals).
  const findScheduleClashes = async (): Promise<string[]> => {
    if (!goalHasTimeRange(form) || !preflightStudents?.length) return [];
    const gStart = toMin(form.startTime);
    const gEnd = toMin(form.endTime);
    const dow = (new Date(`${form.date}T00:00:00Z`).getUTCDay() + 6) % 7; // 0=Mon…6=Sun
    const names: string[] = [];
    for (const s of preflightStudents) {
      try {
        const res = await teacherApi.studentProgram(s.uuid);
        const entries = (res.data?.schedule ?? []).flatMap((sc: any) => sc.study_schedule_entries ?? []);
        const busy = entries.some((e: any) => {
          if (e.day_of_week !== dow || !e.start_time || !e.end_time) return false;
          const st = new Date(e.start_time), en = new Date(e.end_time);
          const eStart = st.getUTCHours() * 60 + st.getUTCMinutes();
          const eEnd = en.getUTCHours() * 60 + en.getUTCMinutes();
          return gStart < eEnd && eStart < gEnd;
        });
        if (busy) names.push(s.name);
      } catch { /* a student we cannot read is not a clash */ }
    }
    return names;
  };

  const submit = async (ignoreSchedule = false) => {
    const problem = validateGoalForm(form);
    if (problem) { setError(problem); return; }
    setSubmitting(true);
    setError(null);
    try {
      if (!ignoreSchedule) {
        const clashes = await findScheduleClashes();
        if (clashes.length) { setScheduleClash(clashes); return; }
      }
      const body = teacherGoalPayload(form);
      if (target.kind === 'student') {
        await teacherApi.assignGoalToStudent(target.uuid, body);
        // Pages that show the program close the modal here and reload it;
        // the others leave it open on the confirmation below.
        setAssigned(true);
        onSubmitted();
      } else {
        const res = await teacherApi.assignGoalToClass(target.classId, body);
        setResult({ succeeded: res.data?.succeeded ?? 0, total: res.data?.total ?? 0, conflicts: res.data?.conflicts ?? [] });
        onSubmitted();
      }
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Hedef atanamadı.');
    } finally {
      setSubmitting(false);
    }
  };

  const heading = target.kind === 'class' ? `${target.name} - Sınıfa Hedef Ata` : `${target.name} - Hedef Ata`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={heading}>
      <div className="absolute inset-0 bg-black/40" onClick={() => !submitting && onClose()} />
      <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[560px] max-h-[90vh] overflow-y-auto p-6" style={F}>
        <button onClick={() => !submitting && onClose()} className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-200 flex items-center justify-center" aria-label="Kapat">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
            <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
          </svg>
        </button>
        <h2 className="text-[20px] font-bold text-[#3973bf] pr-10">{heading}</h2>

        {assigned ? (
          <div className="mt-4 space-y-3">
            <div className="rounded-[14px] bg-[#ecf8ef] border border-[#bfe5c8] p-4">
              <p className="text-[15px] font-bold text-[#27a745]">"{form.title.trim()}" hedefi {target.name} için atandı.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setForm(emptyGoalForm('teacher', form.date, { startTime: form.startTime, endTime: form.endTime })); setAssigned(false); }}
                className="flex-1 rounded-[12px] py-2.5 bg-white border-2 border-[#3973bf] text-[#3973bf] text-[14px] font-bold hover:bg-[#f0f5fc]">
                Bir hedef daha ata
              </button>
              <button onClick={onClose} className="flex-1 rounded-[12px] py-2.5 bg-[#3973bf] hover:bg-[#2f62a6] text-white text-[14px] font-bold">Kapat</button>
            </div>
          </div>
        ) : result ? (
          <div className="mt-4 space-y-3">
            <div className="rounded-[14px] bg-[#ecf8ef] border border-[#bfe5c8] p-4">
              <p className="text-[15px] font-bold text-[#27a745]">{result.succeeded} öğrenciye hedef atandı.</p>
            </div>
            {result.conflicts.length > 0 && (
              <div className="rounded-[14px] bg-[#fff8e1] border border-[#f3dd8f] p-4">
                <p className="text-[14px] font-bold text-[#8a6700] mb-2">{result.conflicts.length} öğrencide o saat dolu, bu öğrenciler atlandı:</p>
                <ul className="space-y-0.5">
                  {result.conflicts.map((c, i) => <li key={i} className="text-[13px] text-[#5c4600]">{c.student}: {c.message}</li>)}
                </ul>
              </div>
            )}
            <button onClick={onClose} className="w-full rounded-[12px] py-2.5 bg-[#3973bf] hover:bg-[#2f62a6] text-white text-[14px] font-bold">Kapat</button>
          </div>
        ) : (
          <>
            <p className="text-[13px] text-gray-500 mt-1 mb-5">
              {target.kind === 'class'
                ? 'Hedef sınıftaki tüm öğrencilere atanır. O saatte başka hedefi olan öğrenciler atlanır.'
                : 'Hedef öğrencinin çalışma planına eklenir.'}
            </p>

            <GoalFormFields
              role="teacher"
              value={form}
              onChange={patch}
              subjects={subjects}
              noSubjectsHint="Branşınıza uygun kitap bulunamadığı için ders listesi boş. Branş/kitap tanımlarını kontrol edin."
              loadKonular={(sid) => teacherApi.kazanims(sid).then((r) => r.data ?? [])}
            />

            {scheduleClash.length > 0 && (
              <div className="mt-4 rounded-[12px] border-2 border-[#e5599b] bg-[#fdecef] p-3">
                <p className="text-[13px] font-bold text-[#c73d74] mb-1">Bu saatte dersi olan öğrenciler:</p>
                <ul className="list-disc pl-5 text-[13px] text-[#2b2b2b]">
                  {scheduleClash.map((n) => <li key={n}>{n}</li>)}
                </ul>
                <p className="text-[12px] text-gray-500 mt-1.5">Başka bir saat seçin, hedefi tüm güne verin ya da yine de atayın.</p>
                <button onClick={() => submit(true)} disabled={submitting}
                  className="mt-2 rounded-[8px] px-4 py-1.5 text-[12px] font-semibold bg-[#e5599b] text-white disabled:opacity-50">
                  Yine de ata
                </button>
              </div>
            )}

            {error && (
              <div className="mt-4 rounded-[10px] px-4 py-2 text-[13px] text-[#c73d74] bg-[#fdecef]" role="alert">{error}</div>
            )}

            <div className="flex gap-3 mt-5">
              <button onClick={() => !submitting && onClose()}
                className="flex-1 rounded-[12px] py-2.5 bg-[#e5e5e5] hover:opacity-90 disabled:opacity-50 text-[14px] font-semibold text-[#2b2b2b]">
                İptal
              </button>
              <button onClick={() => submit(false)} disabled={submitting}
                className="flex-1 rounded-[12px] py-2.5 bg-[#3973bf] hover:bg-[#2f62a6] disabled:opacity-50 text-[14px] font-bold text-white">
                {submitting ? 'Atanıyor…' : target.kind === 'class' ? 'Tüm sınıfa ata' : 'Hedef ata'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HedefAtaModal;
